import { useMemo } from 'react';
import { Alert, Button, Empty, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FleetDriver, PageMeta } from '@goelectric/shared-types';
import { CustomerStatusBadge } from '@/features/clientes/components/CustomerBadges';
import { formatDateTime } from '../utils/format';

export interface FleetDriversTableProps {
  drivers: FleetDriver[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  canWrite: boolean;
  onEdit: (driver: FleetDriver) => void;
  onUnlink: (driver: FleetDriver) => void;
  onRetry: () => void;
  onOffsetChange: (offset: number) => void;
}

/**
 * Tabla de conductores de la flota (US-2.3, ADR 0047). Cada conductor es un cliente
 * `CONDUCTOR` de plataforma: se muestra su estado de cliente (ACTIVE/SUSPENDED), no el
 * estado operativo de la flota. Desvincular no elimina al cliente.
 */
export function FleetDriversTable({
  drivers,
  meta,
  loading,
  error,
  canWrite,
  onEdit,
  onUnlink,
  onRetry,
  onOffsetChange,
}: FleetDriversTableProps) {
  const columns = useMemo<ColumnsType<FleetDriver>>(
    () => [
      {
        title: 'Conductor',
        key: 'driver',
        fixed: 'left',
        width: 260,
        render: (_, driver) => (
          <div className="crm-cell-stack">
            <Typography.Text strong>{driver.fullName}</Typography.Text>
            <Typography.Text
              type="secondary"
              title={driver.customerId}
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {driver.customerId}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Documento',
        key: 'document',
        width: 180,
        render: (_, driver) =>
          driver.documentNumber ? (
            <div className="crm-cell-stack">
              <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{driver.documentNumber}</span>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {driver.documentType ?? '—'}
              </Typography.Text>
            </div>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: 'Contacto',
        key: 'contact',
        width: 230,
        render: (_, driver) => (
          <div className="crm-cell-stack">
            <span>{driver.phone ?? '—'}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {driver.email ?? 'Sin email'}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (_, driver) => <CustomerStatusBadge status={driver.status} />,
      },
      {
        title: 'Vinculado',
        dataIndex: 'linkedAt',
        key: 'linkedAt',
        width: 190,
        render: (value: string) => formatDateTime(value),
      },
      ...(canWrite
        ? [
            {
              title: 'Acciones',
              key: 'actions',
              width: 190,
              align: 'right' as const,
              render: (_: unknown, driver: FleetDriver) => (
                <Space size="small">
                  <Button size="small" onClick={() => onEdit(driver)}>
                    Editar
                  </Button>
                  <Button size="small" danger onClick={() => onUnlink(driver)}>
                    Desvincular
                  </Button>
                </Space>
              ),
            } satisfies ColumnsType<FleetDriver>[number],
          ]
        : []),
    ],
    [canWrite, onEdit, onUnlink],
  );

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar los conductores"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <Table<FleetDriver>
      rowKey="customerId"
      size="small"
      loading={loading}
      columns={columns}
      dataSource={drivers}
      pagination={{
        current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
        pageSize: meta.limit,
        total: meta.total,
        showSizeChanger: false,
        showTotal: (total) => `${total} conductores`,
        onChange: (page) => onOffsetChange((page - 1) * meta.limit),
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Esta flota todavía no tiene conductores vinculados."
          />
        ),
      }}
    />
  );
}
